import RegularShape from 'ol/style/RegularShape';
import Icon from 'ol/style/Icon';
import Circle from 'ol/style/Circle';
import Text from 'ol/style/Text';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Style from 'ol/style/Style';
import { STYLE } from '../../style';
import { getCrossOriginValue } from './getCrossOriginValue';
export const getArcGISSymbolConfig = (metadata, url) => {
    const agsRendererConf = metadata.drawingInfo.renderer;
    const agsSymbol = agsRendererConf.symbol ||
        agsRendererConf.defaultSymbol ||
        agsRendererConf.uniqueValueInfos?.[0].symbol;
    return createSymbolConf(agsSymbol, url);
};
/**
 * Vraci CallBack Style funkci - dle hodnoty jednoho az tri prvku.
 * - pokud nenajde styl pouzije default AGS style z metadat pripadne 'basic' DEFAULT
 */
export const getUniqueValueStyle = (metadata, url) => (feature /*FeatureLike*/) => {
    const agsRendererConf = metadata.drawingInfo.renderer;
    const value = getUniqueValueFromConf(metadata, feature.getProperties());
    const infos = agsRendererConf.uniqueValueInfos;
    let symbolConf;
    if (!infos || typeof value === 'undefined') {
        console.warn(`RendererArcGIS: probably missing configuration for UniqueValue ArcGIS rendering.`);
        return STYLE.DEFAULT;
    }
    // AGS field symbol
    for (let i = 0, len = infos.length; i < len; i++) {
        if (infos[i].value === value) {
            symbolConf = [createSymbolConf(infos[i].symbol, url)];
            break;
        }
    }
    // AGS default symbol
    if (!symbolConf && agsRendererConf.defaultSymbol) {
        symbolConf = [createSymbolConf(agsRendererConf.defaultSymbol, url)];
    }
    // default core symbol
    if (!symbolConf) {
        console.warn(`RendererArcGIS: missing symbol definition for value ${value}`, agsRendererConf);
        return STYLE.DEFAULT;
    }
    return createSymbolStyle(symbolConf);
};
/**
 * Vrati OL Symbol styly na zaklade AGS symbolu
 */
export const createSymbolStyle = (symbolConf) => {
    if (Array.isArray(symbolConf)) {
        return symbolConf.map((config) => {
            return createStyle(config);
        });
    }
    return [];
};
/**
 * Vrati vysledne OL style
 */
export const createStyle = (config) => {
    let style;
    config = config ? { ...config } : { type: '' };
    switch (config.type) {
        case 'none':
        case 'None':
            style = new Style({});
            break;
        case 'Icon':
            let image;
            if (!image) {
                image = new Icon(config);
            }
            // set current rotation to cached Image style
            if (config.rotation) {
                image.setRotation(parseFloat(config.rotation) || 0);
            }
            style = new Style({
                image,
            });
            break;
        case 'Circle':
            style = new Style({
                image: new Circle({
                    fill: new Fill({
                        color: config.fill,
                    }),
                    stroke: new Stroke({
                        color: config.stroke,
                        width: config.width,
                        lineDash: config.lineDash,
                    }),
                    radius: config.radius,
                }),
            });
            break;
        case 'Shape':
            style = new Style({
                fill: new Fill({
                    color: config.fill,
                }),
                stroke: new Stroke({
                    color: config.stroke,
                    width: config.width,
                    lineDash: config.lineDash,
                }),
            });
            break;
        case 'Text':
            style = new Style({
                text: new Text({
                    overflow: true,
                    font: config.font,
                    offsetX: config.offsetX,
                    offsetY: config.offsetY,
                    scale: config.scale,
                    rotation: config.rotation,
                    text: config.text || config.template,
                    textAlign: config.textAlign,
                    textBaseline: config.textBaseline,
                    fill: new Fill({
                        color: config.fill,
                    }),
                    stroke: new Stroke({
                        color: config.stroke,
                        width: config.width,
                    }),
                    placement: config.placement || 'point',
                }),
            });
            break;
        case 'RegularShape':
            style = new Style({
                image: new RegularShape({
                    points: config.points,
                    radius: config.radius,
                    radius1: config.radius1,
                    radius2: config.radius2,
                    angle: config.angle,
                    rotation: config.rotation,
                    fill: new Fill({
                        color: config.fill,
                    }),
                    stroke: new Stroke({
                        color: config.stroke,
                        width: config.width,
                        lineDash: config.lineDash,
                    }),
                }),
            });
            break;
        default:
            console.error('Feature - unknown style: ' + config.type, config);
    }
    return style;
};
/**
 * Na zaklade AGS metadat predpripravi OL objekt pro nastaveni symbolu
 * http://resources.arcgis.com/en/help/arcgis-rest-api/#/Symbol_Objects/02r3000000n5000000/
 */
const createSymbolConf = (agsSymbol, url) => {
    let symbolConf;
    switch (agsSymbol.type) {
        case 'esriSMS': // Simple Marker Symbol
            symbolConf = {
                type: 'Circle',
                fill: agsSymbol.color,
                stroke: agsSymbol.outline ? agsSymbol.outline.color : agsSymbol.color,
                radius: agsSymbol.size,
            };
            break;
        case 'esriSLS': // Simple Line Symbol
            symbolConf = {
                type: 'Shape',
                stroke: agsSymbol.color,
                width: agsSymbol.width,
            };
            break;
        case 'esriSFS': // Simple Fill Symbol
            symbolConf = {
                type: 'Shape',
                fill: agsSymbol.color,
                stroke: agsSymbol.outline ? agsSymbol.outline.color : agsSymbol.color,
                width: agsSymbol.outline ? agsSymbol.outline.width : undefined,
            };
            break;
        case 'esriPMS': {
            // Picture Marker Symbol
            const src = `${url}/images/${agsSymbol.url}`;
            symbolConf = {
                type: 'Icon',
                // AGS pak automaticky do hlavicky odpovedi vlozi
                // "Access-Control-Allow-Origin: ..." => kvuli TISKu
                crossOrigin: getCrossOriginValue(src),
                anchor: [
                    // 0.75 je kompromis pro prepocet z points -> px
                    agsSymbol.xoffset + agsSymbol.width / 0.75 / 2,
                    agsSymbol.yoffset + agsSymbol.height / 0.75 / 2,
                ],
                src,
                anchorXUnits: 'pixels',
                anchorYUnits: 'pixels',
                rotation: agsSymbol.angle,
                opacity: 1,
            };
            break;
        }
        case 'esriTS': // Text Symbol
            symbolConf = {
                type: 'Text',
                fill: agsSymbol.color,
                stroke: agsSymbol.borderLineColor,
                width: agsSymbol.borderLineSize,
                text: agsSymbol.text,
                font: createFont(agsSymbol.font),
                textAlign: agsSymbol.horizontalAlignment,
                textBaseline: agsSymbol.verticalAlignment,
                offsetX: agsSymbol.xoffset,
                offsetY: agsSymbol.yoffset,
            };
            break;
        default:
            console.warn('RendererArcGIS - unkown ArcGIS symbol type ' + agsSymbol.type, agsSymbol);
    }
    return symbolConf;
};
/**
 * Vraci definici fontu z AGS formatu do
 * https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/font
 */
const createFont = (fontConf) => {
    return fontConf.size + 'px ' + fontConf.family;
};
/**
 * Z definice rendereru vezme polozky ve field1-3 a pokud existuji,
 * tak je spoji pomoci fieldDelimiter a vrati jako jeden retezec
 * http://resources.arcgis.com/en/help/arcgis-rest-api/index.html#//02r30000019t000000
 */
const getUniqueValueFromConf = (metadata, props) => {
    const agsRendererConf = metadata.drawingInfo.renderer;
    const values = [];
    for (let i = 1; i <= 3; i++) {
        const fileldName = 'field' + i;
        // get property name by name or alias
        let propertyName = agsRendererConf[fileldName];
        const propertyByAlias = metadata.fields.find((field) => field.alias === propertyName);
        if (propertyByAlias) {
            propertyName = propertyByAlias.name;
        }
        if (propertyName) {
            const propertyValue = props[propertyName];
            values.push(propertyValue);
        }
    }
    return values.join(agsRendererConf.fieldDelimiter);
};
//# sourceMappingURL=arcGISSymbolFactory.js.map